import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { categoryLabel } from "@/components/scam";
import {
  Search, Shield, AlertTriangle, MessageSquare, Link2, Code, Gauge, Users, Ban, Phone, ChevronRight,
} from "lucide-react";

const CATBADGES = [
  "Banka Taklidi", "Kargo / Teslimat", "Bahis / Yatırım", "Asılsız İcra", "Arama / Telesatış",
];

const FEATURES = [
  { icon: Search, title: "Anında Numara Sorgulama", desc: "Herhangi bir numarayı saniyeler içinde sorgulayın, topluluk raporlarını görün." },
  { icon: MessageSquare, title: "Topluluk Yorumları", desc: "Gerçek kullanıcıların deneyimleri ve dolandırıcılık raporları." },
  { icon: Gauge, title: "Risk Skoru", desc: "0-100 arası dinamik risk skoru ile numaranın güvenilirliğini ölçün." },
  { icon: Link2, title: "Link Kısaltma", desc: "Uzun bağlantıları sorgupanel.org/s/xyz formatında kısaltın." },
  { icon: Code, title: "HTML Site Oluşturucu", desc: "Kendi HTML sayfanızı özel slug ile yayınlayın: sorgupanel.org/belirle" },
  { icon: Shield, title: "Anlık Google SEO", desc: "Otomatik sitemap, robots.txt ve tek tıkla Google'a indeks bildirimi." },
];

export default function Home() {
  const [q, setQ] = useState("");
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/stats/overview").then((r) => setStats(r.data)).catch(() => {});
    api.get("/numbers/recent/list").then((r) => setRecent(r.data)).catch(() => {});
  }, []);

  const search = (e) => {
    e.preventDefault();
    const num = q.replace(/[^\d+]/g, "");
    if (num.length >= 3) nav(`/numara/${num}`);
  };

  const ticker = recent.length ? [...recent, ...recent] : [];

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700 text-white overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 sm:pt-24 sm:pb-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-sm font-medium mb-6 backdrop-blur">
              <Shield className="h-4 w-4" /> Türkiye'nin topluluk destekli numara güvenlik platformu
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              Bu numara dolandırıcı mı?
              <br /> <span className="text-sky-300">Sorgula, öğren, koru.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-blue-100 max-w-xl leading-relaxed">
              Şüpheli bir aramanız mı var? Numarayı sorgulayın, topluluğun raporlarını görün ve kendi deneyiminizi paylaşın.
            </p>

            <form onSubmit={search} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl">
              <div className="relative flex-1">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  data-testid="hero-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="0532 XXX XX XX numarasını girin"
                  className="h-14 pl-12 text-base text-slate-900 bg-white border-0 font-mono shadow-xl"
                />
              </div>
              <Button data-testid="hero-search-btn" type="submit" className="h-14 px-8 text-base bg-slate-900 hover:bg-slate-800 shadow-xl">
                <Search className="h-5 w-5 mr-2" /> Sorgula
              </Button>
            </form>

            <div className="mt-6 flex flex-wrap gap-2">
              {CATBADGES.map((c) => (
                <span key={c} className="px-3 py-1 rounded-full bg-white/10 text-xs font-medium backdrop-blur border border-white/10">{c}</span>
              ))}
            </div>
          </motion.div>

          {/* Stats */}
          <div className="mt-14 grid grid-cols-3 gap-4 max-w-2xl">
            {[
              { icon: Search, label: "Sorgulanan Numara", val: stats?.queried_numbers },
              { icon: AlertTriangle, label: "Şüpheli Bildirim", val: stats?.scam_reports },
              { icon: Ban, label: "Toplam Rapor", val: stats?.total_reports },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.1 }}
                className="rounded-xl bg-white/10 backdrop-blur border border-white/15 p-4">
                <s.icon className="h-5 w-5 text-sky-300 mb-2" />
                <div className="text-2xl sm:text-3xl font-extrabold font-display">{s.val ?? "—"}</div>
                <div className="text-xs text-blue-100 mt-1">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Live ticker */}
      {ticker.length > 0 && (
        <section className="bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 py-3 overflow-hidden">
          <div className="flex items-center gap-3 max-w-7xl mx-auto px-4">
            <span className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold text-red-600 uppercase tracking-wider">
              <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" /> Canlı Raporlar
            </span>
            <div className="overflow-hidden flex-1">
              <div className="flex gap-8 animate-marquee whitespace-nowrap w-max">
                {ticker.map((r, i) => (
                  <span key={i} className="text-sm text-slate-500 inline-flex items-center gap-2">
                    <span className={`font-mono font-semibold ${r.vote === "scam" ? "text-red-600" : "text-emerald-600"}`}>{r.number}</span>
                    · {categoryLabel(r.category)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Recent reports grid */}
      {recent.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Son Bildirilen Numaralar</h2>
              <p className="text-slate-500 mt-1">Topluluğun en son eklediği raporlar</p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {recent.slice(0, 9).map((r) => (
              <button
                key={r.id}
                data-testid={`recent-report-${r.id}`}
                onClick={() => nav(`/numara/${r.number}`)}
                className="text-left group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 hover:shadow-md hover:border-blue-300 transition-all"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono font-semibold text-slate-900 dark:text-white">{r.number}</span>
                  {r.vote === "scam" ? (
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-red-50 text-red-600 dark:bg-red-950/40">Dolandırıcı</span>
                  ) : (
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40">Güvenli</span>
                  )}
                </div>
                <div className="text-xs text-blue-600 font-medium mb-1">{categoryLabel(r.category)}</div>
                <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2">{r.comment}</p>
                <div className="mt-3 text-xs text-slate-400 flex items-center gap-1 group-hover:text-blue-600">
                  Detayları gör <ChevronRight className="h-3 w-3" />
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Features */}
      <section className="bg-slate-50 dark:bg-slate-900/40 border-y border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <div className="max-w-2xl mb-10">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Tek platform, tüm araçlar</h2>
            <p className="text-slate-500 mt-2">Numara güvenliğinden SEO uyumlu site oluşturmaya kadar her şey burada.</p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 hover:shadow-md transition-all"
              >
                <div className="h-11 w-11 rounded-xl bg-blue-50 dark:bg-blue-950/40 grid place-items-center mb-4">
                  <f.icon className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="font-display font-semibold text-lg text-slate-900 dark:text-white">{f.title}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 p-8 sm:p-12 text-white flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold">Topluluğa katılın</h2>
            <p className="text-blue-100 mt-2 max-w-lg">Ücretsiz hesap oluşturun; yorum yapın, link kısaltın ve kendi SEO uyumlu sitenizi yayınlayın.</p>
          </div>
          <Button data-testid="cta-register-btn" onClick={() => nav("/register")} className="h-12 px-8 bg-white text-blue-700 hover:bg-blue-50 shadow-xl shrink-0">
            <Users className="h-5 w-5 mr-2" /> Hemen Kayıt Ol
          </Button>
        </div>
      </section>
    </div>
  );
}
